import React from 'react';
import {
    Box,
    Button,
    Divider,
    Grid,
    MenuItem,
    Select,
    Stack,
    styled,
    SwitchProps,
    Typography,
} from '@mui/material';
import {
    createStyleSheet,
    handleError,
    setItemLocalStorage,
    setSessionStorage,
} from 'utils/helpers';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { loginSchema } from 'common/schema';
import BaseTextField from 'common/components/base-text-field';
import BaseTextPasswordField from 'common/components/base-text-password-field';
import { signIn, confirmEmail } from 'services/auth.service';
import { common } from '@mui/material/colors';
import { useNavigate } from 'react-router-dom';
import { ErrorCodes, TOKEN_KEY } from 'common/constants';
import { ILoginSchema } from 'types/auth';
import { useTranslation } from 'react-i18next';
import FormProvider from 'common/components/form-provider';
import { useAppDispatch } from 'common/hook';
import { setIsLoading } from 'slices/loadingSlice';
import { toast } from 'react-toastify';
import { setUser } from 'slices/userSlice';
import background from 'assets/images/backgrounds/new-background.png';
import fbIcon from 'assets/images/icons/fb-icon.png';
import ggIcon from 'assets/images/icons/gg-icon.svg';
import Logo from 'common/components/logo';
import AccountNotActivatedDialog from '../components/account-not-activated-dialog';
import RequiredLabel from 'common/components/required-label';
import { getUserInfo } from 'services/user.service';
import BaseSwitch from 'common/components/base-switch';
import { useTheme } from '@mui/material/styles';
import LanguageIcon from '@mui/icons-material/Language';
import FacebookLogin from '@greatsumini/react-facebook-login';
import useLogin from 'common/hooks/use-login';

const Login = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [showActivateDialog, setShowActivateDialog] = React.useState(false);
    const [language, setLanguage] = React.useState(() => {
        const savedLanguage = localStorage.getItem('i18nextLng') || 'vi';
        return savedLanguage;
    });
    const navigate = useNavigate();
    const dispath = useAppDispatch();
    const methods = useForm({
        resolver: yupResolver(loginSchema),
        defaultValues: {
            email: '',
            password: '',
            rememberMe: true,
        },
    });

    const { i18n } = useTranslation();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        i18n.changeLanguage(e.target.value);
        setLanguage(e.target.value);
        localStorage.setItem('i18nextLng', e.target.value);
    };

    // get url query parameter status_code
    const urlParams = React.useMemo(
        () => new URLSearchParams(window.location.search),
        []
    );
    const confirmToken = urlParams.get('confirm_token');
    const confirmEmailAction = async (token: string) => {
        try {
            const response = await confirmEmail(token);
            if (response.success) {
                toast.success(response.message);
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            handleError(error);
        }
    };
    React.useEffect(() => {
        if (confirmToken) {
            confirmEmailAction(confirmToken);
            // remove confirm_token from url
            const newUrl = window.location.href.split('?')[0];
            window.history.replaceState({}, document.title, newUrl);
        }
    }, [confirmToken, urlParams]);

    const { handleSubmit, setError } = methods;

    const navigateToActivationScreen = (statusCode: number) => {
        if (statusCode === ErrorCodes.CONFIRM_EMAIL) {
            setShowActivateDialog(true);
        }
    };
    const getUserDetail = async () => {
        try {
            const response = await getUserInfo();
            if (response.success) {
                dispath(setUser(response.data));
                return response.data;
            }
        } catch (error) {
            handleError(error);
        }
        return null;
    };

    const onSubmit = handleSubmit(async (user: ILoginSchema) => {
        dispath(setIsLoading(true));
        const dataForm = {
            admin: {
                email: user.email.trim(),
                password: user.password,
            },
        };

        try {
            const response = await signIn(dataForm);
            const { success, status_code, data } = response;
            if (success) {
                dispath(setIsLoading(false));
                if (user.rememberMe) {
                    setItemLocalStorage(TOKEN_KEY, data.token);
                } else {
                    setSessionStorage(TOKEN_KEY, data.token);
                }
                const userDetail = await getUserDetail();
                if (userDetail?.attributes?.role === 'accountant') {
                    navigate('/admin/payment');
                } else {
                    navigate('/');
                }
            } else {
                Object.entries(response?.message).forEach(([key, value]) => {
                    setError(key as keyof ILoginSchema, {
                        type: 'manual',
                        message: value,
                    });
                });
                navigateToActivationScreen(status_code);
                if (status_code === ErrorCodes.ACCOUNT_LOCKED) {
                    toast.error(response?.message);
                }
                dispath(setIsLoading(false));
            }
        } catch (error) {
            if (error instanceof Error) {
                toast.error(error.message);
                dispath(setIsLoading(false));
            }
        }
    });
    const handleDlgClose = (event?: unknown, reason?: string) => {
        if (reason && reason === 'backdropClick') {
            return;
        }

        setShowActivateDialog(false);
    };

    const IOSSwitch = styled((props: SwitchProps) => (
        <BaseSwitch name="rememberMe" {...props} />
    ))(({ theme }) => ({
        width: 48,
        height: 24,
        padding: 0,
        '& .MuiSwitch-switchBase': {
            padding: 0,
            margin: 2,
            transitionDuration: '300ms',
            '&.Mui-checked': {
                transform: 'translateX(22px)',
                color: '#fff',
                '& + .MuiSwitch-track': {
                    backgroundColor: 'var(--color-primary)',
                    opacity: 1,
                    border: 0,
                    ...theme.applyStyles('dark', {
                        backgroundColor: '#2ECA45',
                    }),
                },
                '&.Mui-disabled + .MuiSwitch-track': {
                    opacity: 0.5,
                },
            },
            '&.Mui-focusVisible .MuiSwitch-thumb': {
                color: '#33cf4d',
                border: '6px solid #fff',
            },
            '&.Mui-disabled .MuiSwitch-thumb': {
                color: theme.palette.grey[100],
                ...theme.applyStyles('dark', {
                    color: theme.palette.grey[600],
                }),
            },
            '&.Mui-disabled + .MuiSwitch-track': {
                opacity: 0.7,
                ...theme.applyStyles('dark', {
                    opacity: 0.3,
                }),
            },
        },
        '& .MuiSwitch-thumb': {
            boxSizing: 'border-box',
            width: 22,
            height: 20,
        },
        '& .MuiSwitch-track': {
            borderRadius: 26 / 2,
            backgroundColor: '#E9E9EA',
            opacity: 1,
            transition: theme.transitions.create(['background-color'], {
                duration: 500,
            }),
            ...theme.applyStyles('dark', {
                backgroundColor: '#39393D',
            }),
        },
    }));
    const { loginWithGoogle, loginWithFacebook, onSuccessLoginFacebook } =
        useLogin();
    return (
        <Box sx={styles.loginLayout}>
            <Box
                sx={{
                    flex: 2,
                    backgroundImage: `url(${background})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'bottom',
                    backgroundRepeat: 'no-repeat',
                }}
            ></Box>
            <Box
                sx={{
                    flex: 1,
                    backgroundColor: common.white,
                }}
            >
                <Box padding={6}>
                    <FormProvider methods={methods} onSubmit={onSubmit}>
                        <Grid container>
                            <Grid item xs={12} md={12}>
                                <Logo width={90} height={70} />
                            </Grid>
                            <Grid item xs={12} md={12}>
                                <Typography variant="h3" sx={styles.textLogin}>
                                    {t('common.login')}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={12}>
                                <Stack marginBottom={'17px'} spacing={'7px'}>
                                    <RequiredLabel
                                        label={t('login.userName')}
                                        sx={{ fontSize: '14px', marginLeft: 2 }}
                                        required={false}
                                    />
                                    <BaseTextField
                                        autoComplete="off"
                                        name="email"
                                        fullWidth
                                        size="medium"
                                        placeholder={t(
                                            'login.userName.placeholder'
                                        )}
                                        sx={styles.styleInput}
                                    />
                                </Stack>
                            </Grid>
                            <Grid item xs={12} md={12}>
                                <Stack marginBottom={'17px'} spacing={'7px'}>
                                    <RequiredLabel
                                        label={t('login.password')}
                                        sx={{ fontSize: '14px', marginLeft: 2 }}
                                        required={false}
                                    />
                                    <BaseTextPasswordField
                                        name="password"
                                        fullWidth
                                        size="medium"
                                        placeholder={t(
                                            'login.password.placeholder'
                                        )}
                                        sx={styles.styleInput}
                                        // sx={styleInput}
                                    />
                                </Stack>
                            </Grid>
                            <Grid item xs={12} md={12}>
                                <Stack
                                    marginBottom={'24px'}
                                    direction={'row'}
                                    alignItems={'center'}
                                    justifyContent={'space-between'}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}
                                    >
                                        <IOSSwitch name="rememberMe" />

                                        <Typography sx={{ fontSize: '14px' }}>
                                            {t('login.remember')}
                                        </Typography>
                                    </Box>
                                    <Typography
                                        sx={{
                                            ...styles.textLink,
                                            fontSize: '14px',
                                            color: 'var(--color-primary)',
                                            textUnderlineOffset: '4px',
                                        }}
                                        component={'a'}
                                        onClick={() =>
                                            navigate('/forgot-password')
                                        }
                                    >
                                        {t('login.forgot.password')}
                                    </Typography>
                                </Stack>
                            </Grid>
                        </Grid>

                        <Grid item xs={12} md={12}>
                            <Stack marginBottom={'29px'}>
                                <Button
                                    sx={{
                                        ...styles.buttonLogin,
                                        backgroundColor: 'var(--color-primary)',
                                        fontSize: '14px',
                                        fontWeight: '700',
                                    }}
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                >
                                    {t('common.login')}
                                </Button>
                            </Stack>
                        </Grid>
                        <Divider sx={{ mt: '32px', mb: '32px' }}></Divider>
                        <Grid item xs={12} md={12}>
                            <Stack marginBottom={'29px'} spacing={1}>
                                <Button
                                    onClick={() => loginWithGoogle()}
                                    sx={{
                                        ...styles.buttonLogin,
                                        backgroundColor: '#333333',
                                        fontSize: '14px',
                                        '&:hover': {
                                            backgroundColor: '#333333',
                                        },
                                    }}
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    startIcon={
                                        <img src={ggIcon} alt="Google" />
                                    }
                                >
                                    {t('login.with.google')}
                                </Button>
                                <FacebookLogin
                                    appId={
                                        process.env.REACT_APP_FACEBOOK_APP_ID ||
                                        ''
                                    }
                                    onSuccess={onSuccessLoginFacebook}
                                    onFail={() => {
                                        toast.error(
                                            'Kết nối đến Facebook thất bại, vui lòng thử lại.'
                                        );
                                    }}
                                    scope="public_profile,email"
                                    fields="id,name,email,picture"
                                    onProfileSuccess={(response) => {
                                        loginWithFacebook(response);
                                    }}
                                    render={(renderProps) => (
                                        <Button
                                            onClick={renderProps.onClick}
                                            sx={{
                                                ...styles.buttonLogin,
                                                backgroundColor: '#1877F2',
                                                fontSize: '14px',
                                                '&:hover': {
                                                    backgroundColor: '#1877F2',
                                                },
                                            }}
                                            variant="contained"
                                            color="primary"
                                            fullWidth
                                            startIcon={
                                                <img
                                                    src={fbIcon}
                                                    alt="Facebook"
                                                />
                                            }
                                        >
                                            {t('login.with.facebook')}
                                        </Button>
                                    )}
                                />
                            </Stack>
                        </Grid>
                        <Box textAlign={'center'}>
                            <Typography
                                sx={{
                                    ...styles.textLink,
                                    fontSize: '14px',
                                    fontWeight: 'normal',
                                }}
                                component={'span'}
                            >
                                {t('login.dont.have.account')}
                                <Typography
                                    sx={{
                                        ...styles.textLink,
                                        fontSize: '14px',
                                        color: 'var(--color-primary)',
                                    }}
                                    component={'a'}
                                    onClick={() => navigate('/pre-register')}
                                >
                                    &nbsp;{t('login.sign.up.now')}
                                </Typography>
                            </Typography>
                        </Box>
                        <Select
                            labelId="language"
                            id="language"
                            size="small"
                            value={language}
                            startAdornment={
                                <LanguageIcon
                                    sx={{
                                        color: theme.palette.text.primary,
                                        marginRight: '8px',
                                    }}
                                />
                            }
                            onChange={(event) =>
                                handleChange(
                                    event as React.ChangeEvent<HTMLInputElement>
                                )
                            }
                            sx={{ mt: 10 }}
                        >
                            <MenuItem value={'en'}>English</MenuItem>
                            <MenuItem value={'vi'}>Việt Nam</MenuItem>
                        </Select>
                    </FormProvider>
                </Box>
            </Box>
            {showActivateDialog && (
                <AccountNotActivatedDialog
                    open={showActivateDialog}
                    onClose={handleDlgClose}
                />
            )}
        </Box>
    );
};

const styles = createStyleSheet({
    loginLayout: {
        width: '100vw',
        minHeight: '100vh',
        overflowY: 'auto',
        backgroundAttachment: 'fixed',
        display: 'flex',
    },
    textLogin: {
        fontSize: '20px',
        fontWeight: 'bold',
        lineHeight: '28px',
        marginTop: '48px',
        marginBottom: '24px',
    },
    textLink: {
        cursor: 'pointer',
        fontSize: '20px',
    },
    buttonLogin: {
        lineHeight: '24.2px',
        textTransform: 'none',
        height: 42,
        borderRadius: '6px',
    },
    styleInput: {
        '& .MuiInputBase-root': {
            height: '48px',
            fontSize: '16px',
            backgroundColor: '#F2F2F2',
            borderRadius: '6px',
        },
    },
});

export default Login;
